import { LanguageCodes, TEIComplexity, TemplateID } from '../../shared/enums';
import { Population } from '../../shared/interfaces';
import {
  learningTargets,
  TEST_LEARNING_TARGET_TYPES,
} from '../learning-targets';
import { TEST_HINT_TYPES, chartHints, hints } from '../hints';
import { TEST_IMAGE_TYPES, images } from '../images';
import { TEST_TEXTS_TYPES, texts } from '../texts';
import { TEST_CHOICE_TYPES } from '../choice-types';
import { TestAssociatedSuccessCriteria } from '../test-associated-success-criteria.enum';
import { constructedResponseQuestions } from '../constructed-response-questions';
import { extendedConstructedResponseQuestions } from '../extended-constructed-response-questions';
import { dynamicHorizontalSequencing } from '../dynamic-horizontal-sequencing';
import { staticHorizontalSequencing } from '../static-horizontal-sequencing';
import { staticVerticalSequencing } from '../static-vertical-sequencing';
import { fillInTheBlankTileText } from '../fill-in-the-blank-tile-text-questions';
import { sortingBuckets } from '../sorting-buckets';
import { sortingBucketsImages } from '../sorting-buckets-images';
import { TEST_POPULATION_TYPES, copyHints } from '.';

/*****************************************************************/
/* MATH POPULATIONS                                           */
/*****************************************************************/

const constMathPopParams = {
  product: 'Math',
  language: LanguageCodes.ENGLISH,
};

const constMathPopParamsSCR = {
  templateID: TemplateID.CONSTRUCTED_RESPONSE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.MATH_SHORT_CONSTRUCTED_RESPONSE,
  ],
  domain: { text: 'Math Short Constructed Response domain' },
  standard: { text: 'Math Short Constructed Response standard' },
  learningTarget: [
    learningTargets[TEST_LEARNING_TARGET_TYPES.MATH_SHORT_CONSTRUCTED_RESPONSE],
  ],
  ...constMathPopParams,
};

const constMathPopParamsECR = {
  templateID: TemplateID.EXTENDED_CONSTRUCTED_RESPONSE,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.MATH_EXTENDED_CONSTRUCTED_RESPONSE,
  ],
  domain: { text: 'Math Extended Constructed Response domain' },
  standard: { text: 'Math Extended Constructed Response standard' },
  learningTarget: [
    learningTargets[
      TEST_LEARNING_TARGET_TYPES.MATH_EXTENDED_CONSTRUCTED_RESPONSE
    ],
  ],
  ...constMathPopParams,
};

const constMathPopParamsSCRR = {
  templateID: TemplateID.CONSTRUCTED_RESPONSE_WITH_RUBRIC,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.MATH_SHORT_CONSTRUCTED_RESPONSE_WITH_RUBRIC,
  ],
  domain: { text: 'Math Short Constructed Response Rubric domain' },
  standard: { text: 'Math Short Constructed Response Rubric standard' },
  learningTarget: [
    learningTargets[
      TEST_LEARNING_TARGET_TYPES.MATH_SHORT_CONSTRUCTED_RESPONSE_WITH_RUBRIC
    ],
  ],
  ...constMathPopParams,
};

const constMathPopParamsECRR = {
  templateID: TemplateID.EXTENDED_CONSTRUCTED_RESPONSE_WITH_RUBRIC,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [
    TestAssociatedSuccessCriteria.MATH_EXTENDED_CONSTRUCTED_RESPONSE_WITH_RUBRIC,
  ],
  domain: { text: 'Math Extended Constructed Response Rubric domain' },
  standard: { text: 'Math Extended Constructed Response Rubric standard' },
  learningTarget: [
    learningTargets[
      TEST_LEARNING_TARGET_TYPES.MATH_EXTENDED_CONSTRUCTED_RESPONSE_WITH_RUBRIC
    ],
  ],
  ...constMathPopParams,
};

export const populationsMathSCR: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_M_SCR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_SHORT],
    ...constMathPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_MEDIUM],
    ...constMathPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_LONG],
    ...constMathPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constMathPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constMathPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    ...constMathPopParamsSCR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCRR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCRR_SHORT}`,
    hint: copyHints(chartHints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_SHORT],
    ...constMathPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCRR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCRR_MEDIUM}`,
    hint: copyHints(chartHints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_MEDIUM],
    ...constMathPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCRR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCRR_LONG}`,
    hint: copyHints(chartHints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_LONG],
    ...constMathPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCRR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCRR_PASSAGE_SHORT}`,
    hint: copyHints(chartHints[TEST_HINT_TYPES.DOUBLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constMathPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCRR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCRR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constMathPopParamsSCRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_SCRR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_SCRR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constMathPopParamsSCRR,
  },
};

export const populationsMathECR: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_M_ECR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_SHORT],
    ...constMathPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_MEDIUM],
    ...constMathPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_LONG],
    ...constMathPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constMathPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECR_MEDIUM],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constMathPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    ...constMathPopParamsECR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECRR_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECRR_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_SHORT],
    ...constMathPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECRR_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECRR_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[
        TEST_CHOICE_TYPES.TEST_R_ECRR_MEDIUM
      ],
    ...constMathPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECRR_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECRR_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_LONG],
    ...constMathPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECRR_PASSAGE_SHORT]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECRR_PASSAGE_SHORT}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_SHORT]),
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_SHORT],
    passage: texts[TEST_TEXTS_TYPES.SHORT],
    ...constMathPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECRR_PASSAGE_MEDIUM]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECRR_PASSAGE_MEDIUM}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_MEDIUM]),
    populationData:
      extendedConstructedResponseQuestions[
        TEST_CHOICE_TYPES.TEST_R_ECRR_MEDIUM
      ],
    passage: texts[TEST_TEXTS_TYPES.MEDIUM],
    ...constMathPopParamsECRR,
  },
  [TEST_POPULATION_TYPES.TEST_M_ECRR_PASSAGE_LONG]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_ECRR_PASSAGE_LONG}`,
    hint: copyHints(hints[TEST_HINT_TYPES.SINGLE_LONG]),
    instructions: {
      text: 'This has instructions about something that you should read about here.',
    },
    populationData:
      extendedConstructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_ECRR_LONG],
    passage: texts[TEST_TEXTS_TYPES.LONG],
    image: images[TEST_IMAGE_TYPES.CAT_UNDER_TABLE],
    ...constMathPopParamsECRR,
  },
};

// export const populationsMathDNDSandbox: {
//   [key: number]: Population;
// } = {
//   [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_1]: {
//     id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_1}`,
//     language: LanguageCodes.ENGLISH,
//     product: 'Reading',
//     complexity: 'Comprehension',
//     templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
//     instructions: {
//       text: 'Select the correct answer and hit Ok.',
//     },
//     populationData: fillInTheBlankTileText['test1'],
//     hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
//     feedbackFirstIncorrect: { content: 'Incorrect first!!!'},
//     feedbackSecondIncorrect: { content: 'Incorrect second!!!'},
//   },
//   [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_2]: {
//     id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_2}`,
//     language: LanguageCodes.ENGLISH,
//     product: 'Reading',
//     complexity: 'Comprehension',
//     templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
//     instructions: {
//       text: 'Select the correct answer and hit Ok.',
//     },
//     populationData: fillInTheBlankTileText['test2'],
//     hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
//     feedbackFirstIncorrect: { content: 'Incorrect first!!!'},
//     feedbackSecondIncorrect: { content: 'Incorrect second!!!'},
//   },
//   [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_3]: {
//     id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_3}`,
//     language: LanguageCodes.ENGLISH,
//     product: 'Reading',
//     complexity: 'Comprehension',
//     templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
//     instructions: {
//       text: 'Select the correct answer and hit Ok.',
//     },
//     populationData: fillInTheBlankTileText['test3'],
//     hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
//     feedbackFirstIncorrect: { content: 'Incorrect first!!!'},
//     feedbackSecondIncorrect: { content: 'Incorrect second!!!'},
//   },
//   [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_4]: {
//     id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_4}`,
//     language: LanguageCodes.ENGLISH,
//     product: 'Reading',
//     complexity: 'Comprehension',
//     templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
//     instructions: {
//       text: 'Select the correct answer and hit Ok.',
//     },
//     populationData: fillInTheBlankTileText['test4'],
//     hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
//     feedbackFirstIncorrect: { content: 'Incorrect first!!!'},
//     feedbackSecondIncorrect: { content: 'Incorrect second!!!'},
//   },
//   [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_5]: {
//     id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_5}`,
//     language: LanguageCodes.ENGLISH,
//     product: 'Reading',
//     complexity: 'Comprehension',
//     templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
//     instructions: {
//       text: 'Select the correct answer and hit Ok.',
//     },
//     populationData: fillInTheBlankTileText['test5'],
//     hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
//     feedbackFirstIncorrect: { content: 'Incorrect first!!!'},
//     feedbackSecondIncorrect: { content: 'Incorrect second!!!'},
//   },
// };

const constMathPopParamsDNDFillInBlank = {
  templateID: TemplateID.FILL_IN_THE_BLANK_TILE_TEXT,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.DRAG_AND_DROP],
  domain: { text: 'Math Drag and Drop Fill In Blank domain' },
  standard: { text: 'Math Drag and Drop Fill In Blank standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.DRAG_AND_DROP]],
  ...constMathPopParams,
};

const constMathPopParamsDNDStaticHorzSeq = {
  templateID: TemplateID.STATIC_HORIZONTAL_SEQUENCING,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.DRAG_AND_DROP],
  domain: { text: 'Math Drag and Drop Static Horz Seq domain' },
  standard: { text: 'Math Drag and Drop Static Horz Seq standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.DRAG_AND_DROP]],
  ...constMathPopParams,
};

const constMathPopParamsDNDStaticVertSeq = {
  templateID: TemplateID.STATIC_VERTICAL_SEQUENCING,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.DRAG_AND_DROP],
  domain: { text: 'Math Drag and Drop Static Vert Seq domain' },
  standard: { text: 'Math Drag and Drop Static Vert Seq standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.DRAG_AND_DROP]],
  ...constMathPopParams,
};

const constMathPopParamsDNDSortingBuckets = {
  templateID: TemplateID.SORTING_BUCKETS,
  complexity: 'Application' as TEIComplexity,
  associatedSuccessCriteria: [TestAssociatedSuccessCriteria.DRAG_AND_DROP],
  domain: { text: 'Math Drag and Drop Sorting Buckets domain' },
  standard: { text: 'Math Drag and Drop Sorting Buckets standard' },
  learningTarget: [learningTargets[TEST_LEARNING_TARGET_TYPES.DRAG_AND_DROP]],
  ...constMathPopParams,
};

export const populationsDragAndDropSequencing: { [key: number]: Population } = {
  [TEST_POPULATION_TYPES.TEST_M_DND_STATIC_HORIZ_SEQ]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_STATIC_HORIZ_SEQ}`,
    ...constMathPopParamsDNDStaticHorzSeq,
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    instructions: {
      text: 'Drag the items into the correct order.',
    },
    populationData: staticHorizontalSequencing['test1'],
    feedbackFirstIncorrect: { content: 'You have 1 more try.' },
    feedbackSecondIncorrect: { content: 'The correct answer is...' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_DYNAMIC_HORIZ_SEQ]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_DYNAMIC_HORIZ_SEQ}`,
    ...constMathPopParamsDNDStaticHorzSeq,
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    instructions: {
      text: 'Drag the items into the correct order.',
    },
    populationData: dynamicHorizontalSequencing['test1'],
    feedbackFirstIncorrect: { content: 'You have 1 more try.' },
    feedbackSecondIncorrect: { content: 'The correct answer is...' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_STATIC_VERT_SEQ]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_STATIC_VERT_SEQ}`,
    ...constMathPopParamsDNDStaticVertSeq,
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    instructions: {
      text: 'Drag the items into the correct order.',
    },
    populationData: staticVerticalSequencing['test1'],
    feedbackFirstIncorrect: { content: 'You have 1 more try.' },
    feedbackSecondIncorrect: { content: 'The correct answer is...' },
  },
};

export const populationsDragAndDropFillInTheBlank: {
  [key: number]: Population;
} = {
  [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_1]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_1}`,
    ...constMathPopParamsDNDFillInBlank,
    instructions: {
      text: 'Select the correct answer and hit Ok.',
    },
    populationData: fillInTheBlankTileText['test1'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_2]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_2}`,
    ...constMathPopParamsDNDFillInBlank,
    instructions: {
      text: 'Select the correct answer and hit Ok.',
    },
    populationData: fillInTheBlankTileText['test2'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_3]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_3}`,
    ...constMathPopParamsDNDFillInBlank,
    instructions: {
      text: 'Select the correct answer and hit Ok.',
    },
    populationData: fillInTheBlankTileText['test3'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_4]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_4}`,
    ...constMathPopParamsDNDFillInBlank,
    instructions: {
      text: 'Select the correct answer and hit Ok.',
    },
    populationData: fillInTheBlankTileText['test4'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_5]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_FILL_BLANK_5}`,
    ...constMathPopParamsDNDFillInBlank,
    instructions: {
      text: 'Select the correct answer and hit Ok.',
    },
    populationData: fillInTheBlankTileText['test5'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
};

export const populationsDragAndDropSortingBuckets: {
  [key: number]: Population;
} = {
  [TEST_POPULATION_TYPES.TEST_M_DND_SORTING_BUCKETS_1]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_SORTING_BUCKETS_1}`,
    ...constMathPopParamsDNDSortingBuckets,
    instructions: {
      text: 'Sort answers into the correct category.',
    },
    populationData: sortingBuckets['test1'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_SORTING_BUCKETS_2]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_SORTING_BUCKETS_2}`,
    ...constMathPopParamsDNDSortingBuckets,
    instructions: {
      text: 'Sort answers into the correct category.',
    },
    populationData: sortingBuckets['test2'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
  [TEST_POPULATION_TYPES.TEST_M_DND_SORTING_BUCKETS_3]: {
    id: `${TEST_POPULATION_TYPES.TEST_M_DND_SORTING_BUCKETS_3}`,
    ...constMathPopParamsDNDSortingBuckets,
    instructions: {
      text: 'Sort answers into the correct category.',
    },
    populationData: sortingBuckets['test3'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
};

export const populationsDragAndDropSortingBucketsImages: {
  [key: number]: Population;
} = {
  [TEST_POPULATION_TYPES.TEST_R_DND_SORTING_BUCKETS_IMAGES_1]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SORTING_BUCKETS_IMAGES_1}`,
    ...constMathPopParamsDNDSortingBuckets,
    templateID: TemplateID.SORTING_BUCKETS_IMAGES,
    instructions: {
      text: 'Sort answers into the correct category.',
    },
    populationData: sortingBucketsImages['test1'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
  [TEST_POPULATION_TYPES.TEST_R_DND_SORTING_BUCKETS_IMAGES_2]: {
    id: `${TEST_POPULATION_TYPES.TEST_R_DND_SORTING_BUCKETS_IMAGES_2}`,
    ...constMathPopParamsDNDSortingBuckets,
    templateID: TemplateID.SORTING_BUCKETS_IMAGES,
    instructions: {
      text: 'Sort answers into the correct category.',
    },
    populationData: sortingBucketsImages['test2'],
    hint: [...hints[TEST_HINT_TYPES.SINGLE_LONG]],
    feedbackFirstIncorrect: { content: 'Incorrect first!!!' },
    feedbackSecondIncorrect: { content: 'Incorrect second!!!' },
  },
};
